import React, { useState, useEffect } from 'react';
import { getConfigurations, updateConfiguration } from '../../services/api';
import '../../styles/PromoBundlingManagement.css';

interface Configuration {
  id: number;
  key: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

interface BundlingPromo {
  QUANTITY: number;
  PRICE: number;
  ENABLED: boolean;
}

const PromoBundlingManagement: React.FC = () => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  // Form state for bundling promo
  const [bundlingPromos, setBundlingPromos] = useState<BundlingPromo[]>([
    { QUANTITY: 2, PRICE: 10000, ENABLED: true },
    { QUANTITY: 3, PRICE: 9000, ENABLED: true },
    { QUANTITY: 4, PRICE: 8000, ENABLED: true },
    { QUANTITY: 5, PRICE: 7000, ENABLED: true },
    { QUANTITY: 6, PRICE: 6000, ENABLED: true }
  ]);
  const [defaultPrice, setDefaultPrice] = useState<string>('1000');

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await getConfigurations();
      setConfigurations(configs);
      
      // Set default price from configuration
      const priceConfig = configs.find((config: Configuration) => config.key === 'DEFAULT_PRICE');
      if (priceConfig) {
        setDefaultPrice(priceConfig.value);
      }
      
      // Set bundling promo from configuration
      const bundlingConfig = configs.find((config: Configuration) => config.key === 'PROMO_TABLE');
      if (bundlingConfig && bundlingConfig.value) {
        try {
          const bundlingValue = JSON.parse(bundlingConfig.value) as BundlingPromo[];
          setBundlingPromos(bundlingValue);
        } catch (err) {
          console.error('Failed to parse PROMO_TABLE value:', err);
          // Set default values if parsing fails
          setBundlingPromos([
            { QUANTITY: 2, PRICE: 10000, ENABLED: true },
            { QUANTITY: 3, PRICE: 9000, ENABLED: true },
            { QUANTITY: 4, PRICE: 8000, ENABLED: true },
            { QUANTITY: 5, PRICE: 7000, ENABLED: true },
            { QUANTITY: 6, PRICE: 6000, ENABLED: true }
          ]);
        }
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch configurations:', err);
      setError(err.response?.data?.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBundling = async () => {
    if (!bundlingPromos.length) {
      setError('Please configure at least one bundling promo');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Update the configuration
      await updateConfiguration('PROMO_TABLE', JSON.stringify(bundlingPromos));
      
      setSuccess('Bundling promo updated successfully');
      setIsEditing(false);
      
      // Refresh configurations
      await fetchConfigurations();
    } catch (err: any) {
      console.error('Failed to update bundling promo:', err);
      setError(err.response?.data?.message || 'Failed to update bundling promo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const newBundlingPromos = [...bundlingPromos];
    newBundlingPromos[index].QUANTITY = parseInt(value) || 0;
    setBundlingPromos(newBundlingPromos);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const newBundlingPromos = [...bundlingPromos];
    newBundlingPromos[index].PRICE = parseInt(value) || 0;
    setBundlingPromos(newBundlingPromos);
  };

  const handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newBundlingPromos = [...bundlingPromos];
    newBundlingPromos[index].ENABLED = e.target.checked;
    setBundlingPromos(newBundlingPromos);
  };

  const formatPrice = (price: string): string => {
    return `Rp ${parseInt(price || '0').toLocaleString('id-ID')}`;
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to original values
    const bundlingConfig = configurations.find((config: Configuration) => config.key === 'PROMO_TABLE');
    if (bundlingConfig && bundlingConfig.value) {
      try {
        const bundlingValue = JSON.parse(bundlingConfig.value) as BundlingPromo[];
        setBundlingPromos(bundlingValue);
      } catch (err) {
        // Keep current values if parsing fails
      }
    }
  };

  if (loading && configurations.length === 0) {
    return (
      <div className="promo-bundling-management">
        <h3>Bundling Promo</h3>
        <div className="loading-indicator">Loading configurations...</div>
      </div>
    );
  }

  return (
    <div className="promo-bundling-management">
      <h3>Bundling Promo</h3>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="bundling-description">
        <p>
          Set up bundling promos where customers get special pricing based on the quantity purchased.
          The highest applicable bundling tier will be applied to the purchase.
        </p>
      </div>
      
      {!isEditing ? (
        <div className="bundling-display">
          <div className="bundling-info">
            <table className="bundling-table">
              <thead>
                <tr>
                  <th>Quantity</th>
                  <th>Price per video</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(bundlingPromos) && bundlingPromos.map((promo, index) => (
                  <tr key={index}>
                    <td>{promo.QUANTITY} videos</td>
                    <td>{formatPrice(promo.PRICE.toString())}</td>
                    <td>{promo.ENABLED ? 'Enabled' : 'Disabled'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <button 
            className="edit-button" 
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            Edit Bundling
          </button>
        </div>
      ) : (
        <div className="bundling-edit">
          <table className="bundling-edit-table">
            <thead>
              <tr>
                <th>Quantity</th>
                <th>Price per video (IDR)</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(bundlingPromos) && bundlingPromos.map((promo, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="number"
                      className="quantity-input"
                      value={promo.QUANTITY}
                      onChange={(e) => handleQuantityChange(e, index)}
                      min="1"
                      max="6"
                      disabled={submitting}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="price-input"
                      value={promo.PRICE}
                      onChange={(e) => handlePriceChange(e, index)}
                      placeholder="Enter price in IDR"
                      disabled={submitting}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={promo.ENABLED}
                      onChange={(e) => handleEnabledChange(e, index)}
                      disabled={submitting}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="button-group">
            <button
              className="cancel-button"
              onClick={handleCancelEdit}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="save-button"
              onClick={handleUpdateBundling}
              disabled={
                submitting || 
                !Array.isArray(bundlingPromos) ||
                bundlingPromos.length === 0 || 
                bundlingPromos.some(promo => promo.QUANTITY <= 0 || promo.PRICE < 0)
              }
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoBundlingManagement; 